"use client";

import { useMemo, useState } from "react";
import Image from "next/image";

type BeforeItem = { text: string; image?: string };
type AfterItem = { text: string; image: string };

type Pair = {
  before: BeforeItem;
  after: AfterItem | null;
};

export default function BeforeAfterCarousel({
  beforeItems,
  afterItems,
}: {
  beforeItems: BeforeItem[];
  afterItems: AfterItem[];
}) {
  const pairs = useMemo<Pair[]>(
    () =>
      beforeItems.map((before, index) => ({
        before,
        after: afterItems[index] ?? null,
      })),
    [beforeItems, afterItems],
  );

  const [activeIndex, setActiveIndex] = useState(0);
  const total = pairs.length;
  const current = pairs[activeIndex];

  const previous = () => setActiveIndex((prev) => (prev - 1 + total) % total);
  const next = () => setActiveIndex((prev) => (prev + 1) % total);

  if (!current) return null;

  return (
    <div className="mt-5 overflow-hidden rounded-xl border border-zinc-800/90 bg-gradient-to-br from-zinc-950 via-zinc-950 to-black shadow-[0_16px_36px_-28px_rgba(0,0,0,0.9)]">
      <div className="flex items-center justify-between border-b border-zinc-800 bg-zinc-900/70 px-3 py-2.5">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-500">
          Pair {String(activeIndex + 1).padStart(2, "0")}
        </p>
        <div className="flex items-center gap-2">
          <span className="inline-flex rounded-full border border-rose-500/35 bg-rose-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-rose-300">
            Before
          </span>
          <span className="inline-flex rounded-full border border-emerald-500/35 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-300">
            After
          </span>
        </div>
      </div>

      <div className="grid gap-3 border-b border-zinc-800/80 p-3 md:grid-cols-2">
        <div className="space-y-2">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-rose-300">Before</p>
          <div className="relative aspect-[16/10] w-full overflow-hidden rounded-md border border-rose-500/25 bg-zinc-900">
            {current.before.image ? (
              <Image
                src={current.before.image}
                alt={current.before.text}
                fill
                sizes="(max-width: 767px) 90vw, 45vw"
                className="object-cover"
                fetchPriority="low"
                decoding="async"
              />
            ) : null}
            <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/95 via-black/60 to-transparent p-2">
              <p className="text-[11px] leading-snug font-medium text-zinc-100">{current.before.text}</p>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-300">After</p>
          <div className="relative aspect-[16/10] w-full overflow-hidden rounded-md border border-emerald-500/25 bg-zinc-900">
            {current.after ? (
              <Image
                src={current.after.image}
                alt={current.after.text}
                fill
                sizes="(max-width: 767px) 90vw, 45vw"
                className="object-cover"
                fetchPriority="low"
                decoding="async"
              />
            ) : null}
            <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/95 via-black/60 to-transparent p-2">
              <p className="text-[11px] leading-snug font-medium text-zinc-100">
                {current.after ? current.after.text : "-"}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between px-3 py-2.5">
        <button
          type="button"
          onClick={previous}
          className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-zinc-700 text-zinc-200 hover:border-[#b89a5b]/70 hover:text-[#b89a5b]"
          aria-label="Show previous comparison"
        >
          <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" aria-hidden>
            <path d="M11.5 5.5L7 10l4.5 4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <p className="text-[11px] text-zinc-500">
          {activeIndex + 1} / {total}
        </p>
        <button
          type="button"
          onClick={next}
          className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-zinc-700 text-zinc-200 hover:border-[#b89a5b]/70 hover:text-[#b89a5b]"
          aria-label="Show next comparison"
        >
          <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" aria-hidden>
            <path d="M8.5 5.5L13 10l-4.5 4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}
